import { useMutation } from "@tanstack/react-query";
import { FirstStepFormData } from "../../schemas/validationSchemas";
import { calculatePumpRecommendations } from "../useFormPoolCalculations";
import { calculateSurfaceArea } from "../../utils/calculateSurface";
import { SeasonTypes, PoolType } from "../../context/types";
import { useCreateSalesforceRecord } from "./useSalesforce";
import { CreateReportBodyRequest } from "../../types/services/salesforce";

interface PoolCalculationResult {
  reportId: string;
  formData: FirstStepFormData;
  recommendations: any;
}

interface PoolCalculationError {
  message: string;
  phase: "calculation" | "salesforce";
}

const transformFormDataToSalesforcePayload = (
  data: FirstStepFormData,
  recommendations: any,
  surfaceAreaTop: number,
  volume: number
): CreateReportBodyRequest => {
  const determineSeason = (months: string[]): SeasonTypes => {
    if (months.length === 12) return SeasonTypes.Full;
    if (months.length >= 6 && months.length <= 8) return SeasonTypes.Summer;
    if (months.length >= 9 && months.length <= 11) return SeasonTypes.Extended;
    return SeasonTypes.Custom;
  };

  const season = determineSeason(data.operatingMonths);

  const temperaturePerMonth = data.temperatureData
    ? JSON.stringify(data.temperatureData)
    : JSON.stringify({});

  return {
    avarageDepth: data.depth.toString(),
    diameter: (data.diameter || 0).toString(),
    length: (data.length || 0).toString(),
    width: (data.width || 0).toString(),
    poolLocation: data.location!,
    city: data.city,
    postCode: data.postcode,
    big: recommendations.premium.capacity,
    small: recommendations.standard.capacity,
    existingPool: data.isExistingPool || false,
    standardPumpCapacity: recommendations.standard.capacity,
    premiumPumpCapacity: recommendations.premium.capacity,
    electricityCost: data.electricityCost,
    operationHours: data.operatingHours,
    email: data.email,
    name: data.user,
    volume: volume,
    poolMaterial: data.poolMaterial!,
    poolType: data.isExistingPool ? PoolType.Inground : PoolType.Inground,
    requiredPoolTemp: data.requirePoolTemp,
    poolShape: data.shape.toString(),
    state: data.state,
    season: season,
    surfaceArea: surfaceAreaTop,
    temperaturePerMonth: temperaturePerMonth,
    totalPricePremium: recommendations.premium.totalPrice,
    totalPriceStandart: recommendations.standard.totalPrice,
    withCover: data.withCover === "Yes" ? "Yes" : "No",
    token: "",
  };
};

export const usePoolCalculationSubmission = () => {
  const createSalesforceRecord = useCreateSalesforceRecord();

  return useMutation<
    PoolCalculationResult,
    PoolCalculationError,
    FirstStepFormData
  >({
    mutationFn: async (
      data: FirstStepFormData
    ): Promise<PoolCalculationResult> => {
      try {
        const recommendations = calculatePumpRecommendations(data);
        const poolInfo = {
          shape: data.shape,
          depth: data.depth,
          diameter: data.diameter || 0,
          length: data.length || 0,
          width: data.width || 0,
          surfaceArea: data.surfaceArea || 0,
          location: data.location,
          poolMaterial: data.poolMaterial,
          poolType: data.isExistingPool ? "Existing" : "New",
          requirePoolTemp: data.requirePoolTemp,
          withCover: data.withCover === "Yes",
          operatingHours: data.operatingHours,
          electricityCost: data.electricityCost,
          country: data.country,
          state: data.state,
          city: data.city,
          postcode: data.postcode,
        };

        const { surfaceAreaTop, volume } = calculateSurfaceArea({
          info: poolInfo as any,
        });

        const salesforcePayload = transformFormDataToSalesforcePayload(
          data,
          recommendations,
          surfaceAreaTop,
          volume
        );

        const salesforceResponse = await new Promise<any>((resolve, reject) => {
          createSalesforceRecord.mutate(salesforcePayload, {
            onSuccess: resolve,
            onError: reject,
          });
        });

        const reportId =
          salesforceResponse.id ||
          salesforceResponse.reportId ||
          salesforceResponse.Id ||
          salesforceResponse.data.id;

        return {
          reportId,
          formData: data,
          recommendations,
        };
      } catch (error) {
        console.error("❌ Error in pool calculation submission:", error);

        const errorPhase =
          error instanceof Error && error.message.includes("Salesforce")
            ? "salesforce"
            : "calculation";

        const calculationError: PoolCalculationError = {
          message:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred",
          phase: errorPhase,
        };
        throw calculationError;
      }
    },
    onSuccess: (result) => {
      console.log(
        "✅ Pool calculation submission completed successfully:",
        result
      );
    },
    onError: (error) => {
      console.error(`❌ Error in ${error.phase} phase:`, error.message);
    },
  });
};
